/**
 * Enhanced Language Detector with Location and Device Detection
 * Detects language based on:
 * 1. User preference (localStorage)
 * 2. Browser language (navigator.language)
 * 3. Device location (IP-based geolocation)
 * 4. Timezone
 */

// Country to language mapping
// Note: For multilingual countries (like Belgium), browser language takes precedence
const countryToLanguage = {
  // Belgium - multilingual (Dutch, French, German)
  // Browser language will be checked first
  BE: null, // Will use browser language or default to 'nl'
  
  // Netherlands
  NL: 'nl',
  
  // France
  FR: 'fr',
  
  // Germany
  DE: 'de',
  
  // Spain
  ES: 'es',
  
  // Italy
  IT: 'it',
  
  // Portugal
  PT: 'pt',
  
  // UK
  GB: 'en',
  
  // Other English-speaking countries
  US: 'en',
  CA: 'en',
  AU: 'en',
  NZ: 'en',
  IE: 'en',
  
  // French-speaking countries
  CH: 'fr', // Switzerland (French)
  LU: 'fr', // Luxembourg
  MC: 'fr', // Monaco
  
  // German-speaking countries
  AT: 'de', // Austria
  LI: 'de', // Liechtenstein
  
  // Spanish-speaking countries
  MX: 'es',
  AR: 'es',
  CO: 'es',
  CL: 'es',
  PE: 'es',
  VE: 'es',
  EC: 'es',
  BO: 'es',
  PY: 'es',
  UY: 'es',
  CR: 'es',
  PA: 'es',
  DO: 'es',
  GT: 'es',
  HN: 'es',
  NI: 'es',
  SV: 'es',
  CU: 'es',
  
  // Portuguese-speaking countries
  BR: 'pt',
  AO: 'pt',
  MZ: 'pt',
  
  // Italian-speaking countries
  SM: 'it', // San Marino
  VA: 'it', // Vatican
};

// Timezone to language mapping (fallback)
const timezoneToLanguage = {
  'Europe/Amsterdam': 'nl',
  'Europe/Brussels': 'nl',
  'Europe/Paris': 'fr',
  'Europe/Berlin': 'de',
  'Europe/Madrid': 'es',
  'Europe/Rome': 'it',
  'Europe/Lisbon': 'pt',
  'Europe/London': 'en',
};

// Supported languages
const supportedLanguages = ['en', 'nl', 'fr', 'de', 'es', 'it', 'pt'];

/**
 * Get language from browser
 */
function getBrowserLanguage() {
  if (typeof navigator === 'undefined') return null;
  
  // Get primary language
  const primaryLang = navigator.language || navigator.userLanguage;
  if (primaryLang) {
    const langCode = primaryLang.split('-')[0].toLowerCase();
    if (supportedLanguages.includes(langCode)) {
      return langCode;
    }
  }
  
  // Check all languages
  if (navigator.languages) {
    for (const lang of navigator.languages) {
      const langCode = lang.split('-')[0].toLowerCase();
      if (supportedLanguages.includes(langCode)) {
        return langCode;
      }
    }
  }
  
  return null;
}

/**
 * Get language from timezone
 */
function getTimezoneLanguage() {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezoneToLanguage[timezone] || null;
  } catch (error) {
    return null;
  }
}

/**
 * Detect country from IP using free geolocation API
 * Uses ipapi.co (free tier: 1000 requests/day)
 */
async function detectCountryFromIP() {
  try {
    // Try multiple free geolocation services
    const services = [
      'https://ipapi.co/json/',
      'https://ip-api.com/json/?fields=countryCode',
      'https://api.ipgeolocation.io/ipgeo?apiKey=free', // May require API key
    ];
    
    for (const service of services) {
      try {
        // Create timeout controller
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        const response = await fetch(service, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code || data.countryCode || data.country;
          
          if (countryCode && typeof countryCode === 'string') {
            return countryCode.toUpperCase();
          }
        }
      } catch (error) {
        // Try next service
        continue;
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Could not detect country from IP:', error);
    return null;
  }
}

/**
 * Get language from country code
 */
function getLanguageFromCountry(countryCode) {
  if (!countryCode) return null;
  
  const lang = countryToLanguage[countryCode];
  return lang || null;
}

/**
 * Main detection function
 * Returns detected language code
 */
export async function detectLanguage() {
  // 1. Check localStorage first (user preference)
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('i18nextLng');
    if (stored && supportedLanguages.includes(stored)) {
      return stored;
    }
  }
  
  // 2. Check browser language
  const browserLang = getBrowserLanguage();
  
  // 3. Try to detect from location (IP-based)
  try {
    const countryCode = await detectCountryFromIP();
    if (countryCode) {
      // Store detected country for future use
      if (typeof window !== 'undefined') {
        localStorage.setItem('detectedCountry', countryCode);
      }
      
      const locationLang = getLanguageFromCountry(countryCode);
      
      // For multilingual countries (BE, CH, etc.), prefer browser language
      if (countryCode === 'BE' || countryCode === 'CH' || countryCode === 'LU') {
        // If browser language is available and matches a supported language, use it
        if (browserLang && supportedLanguages.includes(browserLang)) {
          return browserLang;
        }
        // Otherwise, use location-based default
        return locationLang || 'nl'; // Default for Belgium
      }
      
      // For other countries, use location-based language if available
      if (locationLang) {
        return locationLang;
      }
    }
  } catch (error) {
    console.warn('Location detection failed:', error);
  }
  
  // 4. Fallback to browser language (if location detection failed)
  if (browserLang) {
    return browserLang;
  }
  
  // 4. Fallback to timezone
  const timezoneLang = getTimezoneLanguage();
  if (timezoneLang) {
    return timezoneLang;
  }
  
  // 5. Final fallback to English
  return 'en';
}

/**
 * Initialize language detection on app start
 */
export async function initializeLanguageDetection(i18n) {
  try {
    const detectedLang = await detectLanguage();
    
    // Only change language if not already set by user
    const currentLang = i18n.language || i18n.languages?.[0];
    const hasUserPreference = localStorage.getItem('i18nextLng');
    
    if (!hasUserPreference && detectedLang && detectedLang !== currentLang) {
      await i18n.changeLanguage(detectedLang);
      console.log(`🌍 Language auto-detected: ${detectedLang}`);
    }
  } catch (error) {
    console.error('Error initializing language detection:', error);
  }
}

export default {
  detectLanguage,
  initializeLanguageDetection,
  getBrowserLanguage,
  getTimezoneLanguage,
  getLanguageFromCountry,
};

